import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
    AuthenticatorService,
    Permission,
    UserType,
} from './../../../Services/authenticator.service';
import { Aspirante } from 'src/app/Shared/Interfaces/aspirante.interface';
import { DatosGenerales } from '../Interfaces/datos-generales.interface';
import { DatosSocioeconomicos } from '../Interfaces/datos-socioeconomicos.interface';
import jsPDF from 'jspdf';
import moment from 'moment';
import autoTable, { UserOptions } from 'jspdf-autotable';
import { SolConfiguracion } from '../Interfaces/sol-configuracion.interface';
import { Carrera } from 'src/app/Shared/Interfaces/carrera.interface';
import { EntidadFederativa } from 'src/app/Shared/Interfaces/entidad-federativa';

export type PDF = {
    file: Blob;
    filename: string;
};

type Datos = {
    asp: Aspirante;
    gen: DatosGenerales;
    socieco: DatosSocioeconomicos;
    conf: SolConfiguracion;
    prepa: any;
    carreras: Carrera[];
    entidadesFederativas: EntidadFederativa[];
};

@Injectable({
    providedIn: 'root',
})
export class AspiranteService {
    private base_url: string = environment.baseUrl;

    parentesco: string[] = [
        'Padre',
        'Madre',
        'Hermano',
        'Hermana',
        'Cónyuge o pareja',
        'Hijo',
        'Hija',
        'Otro familiar',
        'Amigo',
        'Amiga',
        'Otro',
    ];

    constructor(private http: HttpClient, private auth: AuthenticatorService) {}

    getAspirante(
        numeroSolicitud?: string,
        periodo?: string
    ): Observable<Aspirante[]> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-asps`;
        const options = { headers };

        const filter = {
            where: {
                no_solicitud: numeroSolicitud,
                periodo,
            },
        };

        const params = new HttpParams().set('filter', JSON.stringify(filter));
        options['params'] = params;

        return this.http.get<Aspirante[]>(url_api, options);
    }

    updateAspirante(
        numeroSolicitud: string,
        periodo: string,
        alumnoAttrs: Partial<Aspirante>
    ) {
        const headers = this.auth.headers;
        const options = { headers };

        const where = {
            no_solicitud: numeroSolicitud,
            periodo,
        };

        const params = new HttpParams().set('where', JSON.stringify(where));
        options['params'] = params;

        const url_api = `${this.base_url}/sol-ficha-asps/`;

        return this.http.patch<Aspirante>(url_api, alumnoAttrs, options);
    }

    getAspiranteAll(filter?: any): Observable<Aspirante[]> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-asps`;
        const options = { headers };

        if (filter) {
            const params = new HttpParams().set(
                'filter',
                JSON.stringify(filter)
            );
            options['params'] = params;
        }

        return this.http.get<Aspirante[]>(url_api, options);
    }

    getAspirantePeriodo(periodo: string): Observable<Aspirante[]> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/aspirantes/${periodo}`;
        const options = { headers };

        return this.http.get<Aspirante[]>(url_api, options);
    }

    getAceptadosPeriodoPromise(periodo: string): Promise<any[]> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/aspirantes/aceptados/${periodo}`;
        const options = { headers };

        return this.http.get<Aspirante[]>(url_api, options).toPromise();
    }

    getAceptadosPogradoPeriodoPromise(periodo: string): Promise<any[]> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/aspirantes-posgrado/aceptados/${periodo}`;
        const options = { headers };

        return this.http.get<Aspirante[]>(url_api, options).toPromise();
    }

    existeSol(curp: string, periodo: string): Observable<Permission> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-asps/existe/${periodo}/${curp}`;
        const options = { headers };

        return this.http.get<Permission>(url_api, options);
    }

    createAcceso(asp: {
        no_solicitud: number;
        periodo: string;
        password: string;
    }): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-accesos`;
        const options = { headers };

        return this.http.post<any>(url_api, asp, options);
    }

    updateAcceso(
        asp: {
            no_solicitud: number;
            password: string;
        },
        no_solicitud: number,
        periodo: string
    ): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-accesos`;
        const options = { headers };

        const where = {
            no_solicitud,
            periodo,
        };

        const params = new HttpParams().set('where', JSON.stringify(where));
        options['params'] = params;

        return this.http.patch<any>(url_api, asp, options);
    }

    createSol(asp): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-asps`;
        const options = { headers };

        return this.http.post<Aspirante>(url_api, asp, options);
    }

    patchGeneralData(
        numeroSolicitud: string,
        periodo: string,
        asp: Partial<DatosGenerales>
    ): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-asp-dats`;
        const options = { headers };
        const where = {
            no_solicitud: numeroSolicitud,
            periodo,
        };

        const params = new HttpParams().set('where', JSON.stringify(where));
        options['params'] = params;

        return this.http.patch<any>(url_api, asp, options);
    }

    patchSociData(
        numeroSolicitud: string,
        periodo: string,
        asp: Partial<DatosSocioeconomicos>
    ): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-ficha-asp-dses`;
        const options = { headers };
        const where = {
            no_solicitud: numeroSolicitud,
            periodo,
        };

        const params = new HttpParams().set('where', JSON.stringify(where));
        options['params'] = params;

        return this.http.patch<any>(url_api, asp, options);
    }

    createFicha(asp: {
        no_solicitud: number;
        periodo: string;
        no_ficha: number;
        no_recibo: number;
        fecha_pago: Date;
    }): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-fichas-asp-examen`;
        const options = { headers };

        return this.http.post<any>(url_api, asp, options);
    }

    createFichaPosgrado(asp: {
        no_solicitud: number;
        periodo: string;
        no_ficha: number;
        no_recibo_curso?: number;
        fecha_pago_curso?: Date;
        no_recibo_examen?: number;
        fecha_pago_examen?: Date;
    }): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-fichas-asp-examen-posgrados`;
        const options = { headers };

        return this.http.post<any>(url_api, asp, options);
    }

    updateFichaPosgrado(
        numeroSolicitud: string,
        periodo: string,
        asp: Partial<{
            no_solicitud: number;
            periodo: string;
            no_ficha: number;
            no_recibo_curso?: number;
            fecha_pago_curso?: Date;
            no_recibo_examen?: number;
            fecha_pago_examen?: Date;
            aceptado?: boolean;
        }>
    ): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-fichas-asp-examen-posgrados`;
        const options = { headers };

        const where = {
            and: [{ no_solicitud: numeroSolicitud }, { periodo }],
        };

        const params = new HttpParams().set('where', JSON.stringify(where));
        options['params'] = params;

        return this.http.patch<any>(url_api, asp, options);
    }

    getFichaPosgrado(no_solicitud, periodo): Observable<any> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/sol-fichas-asp-examen-posgrados`;
        const options = { headers };

        const where = {
            no_solicitud,
            periodo,
        };

        const filter = { where };

        const params = new HttpParams().set('filter', JSON.stringify(filter));
        options['params'] = params;

        return this.http.get<any>(url_api, options);
    }

    postAeptados(pagos: any, periodo: string): Observable<object[]> {
        const headers = this.auth.headers;
        const url_api = `${this.base_url}/aspirantes-excel/${periodo}`;
        return this.http.post<object[]>(url_api, pagos, { headers });
    }

    generarPDF(datos: Datos): PDF {
        const doc = new jsPDF();
        moment.locale('es-mx');

        const fechaRegistro: string = moment(datos.asp.fecha_registro).format(
            'dddd LL[, a las ]LT'
        );

        doc.setFontSize(7);

        doc.text(`REGISTRADO EL ${fechaRegistro.toUpperCase()}.`, 15, 285);

        const solicitud: string = this.acompletarCeros(datos.asp.no_solicitud);
        const nombre: string =
            datos.asp.apellido_paterno +
            ' ' +
            datos.asp.apellido_materno +
            ' ' +
            datos.asp.nombre_aspirante;
        const curp: string = datos.asp.curp;
        const colonia: string = datos.gen.colonia;
        const calle_no: string = datos.gen.calle_no;
        const codigo_postal = datos.gen.codigo_postal;
        const estado: string =
            datos.entidadesFederativas.find(
                (ef) => ef.clave == datos.gen.entidad_federativa
            )?.entidad_federativa ?? '';
        const municipio: string = datos.gen.municipio;
        const telefono: string = datos.asp.telefono;
        const clave_prepa = datos.gen.clave_preparatoria;
        const prepa = datos.prepa.nombre_preparatoria;
        const op1: string =
            datos.carreras.find(
                (c) =>
                    c.carrera == datos.gen.carrera_opcion_1 &&
                    c.reticula == datos.gen.reticula_opcion_1
            )?.nombre_carrera ?? '';
        const op2: string =
            datos.carreras.find(
                (c) =>
                    c.carrera == datos.gen.carrera_opcion_2 &&
                    c.reticula == datos.gen.reticula_opcion_2
            )?.nombre_carrera ?? '';
        const fechaInicial: string = moment(datos.conf.fecha_inicio).format(
            'dddd LL'
        );

        const fechaFinal: string = moment(datos.conf.fecha_termino).format(
            'dddd LL'
        );

        const costo: number = datos.conf.costo_examen;
        const banco: string = datos.conf.banco;
        const cuenta: string = datos.conf.cuenta;

        const fechaExamen: string = moment(datos.conf.fecha_examen).format(
            'dddd LL'
        );

        const horaExamen: string = moment(
            '2013-02-08 ' + datos.conf.hora_examen
        ).format('LT');

        let table: UserOptions = {
            margin: { top: 15 },
            headStyles: {
                halign: 'center',
                valign: 'middle',
                minCellHeight: 5,
                cellPadding: 0.1,
                // lineWidth: 0.1,
                // lineColor: 'black',
                fillColor: 'white',
                textColor: 'black',
                fontSize: 8,
            },
            bodyStyles: {
                textColor: 'black',
                fontSize: 10,
                fillColor: 'white',
                // lineWidth: 0.1,
                // lineColor: 'black',
            },
            head: [
                [
                    {
                        content: '',
                        rowSpan: 3,
                        colSpan: 5,
                    },
                    {
                        content: '',
                        rowSpan: 3,
                        colSpan: 4,
                    },
                    {
                        content: '',
                        rowSpan: 3,
                        colSpan: 2,
                    },
                    {
                        content:
                            'Secretaria de Educación Pública'.toUpperCase(),
                        rowSpan: 1,
                        colSpan: 9,
                        styles: {
                            valign: 'bottom',
                            halign: 'right',
                        },
                    },
                ],
                [
                    {
                        content: 'Tecnológico Nacional de México'.toUpperCase(),
                        rowSpan: 1,
                        colSpan: 9,
                        styles: {
                            valign: 'middle',
                            halign: 'right',
                        },
                    },
                ],
                [
                    {
                        content:
                            'Instituto Tecnológico de Tehuacán'.toUpperCase(),
                        rowSpan: 1,
                        colSpan: 9,
                        styles: {
                            valign: 'top',
                            halign: 'right',
                        },
                    },
                ],
            ],
            didDrawCell: (data) => {
                console.log(data);
                if (
                    (data.column.index == 0 ||
                        data.column.index == 5 ||
                        data.column.index == 9) &&
                    data.row.index == 0 &&
                    data.cell.section == 'head'
                ) {
                    if (data.column.index == 0) {
                        var img =
                            '../../../../../../assets/img/logo_educacion_sep.png';
                    } else if (data.column.index == 5) {
                        var img = '../../../../../../assets/img/logo_tecnm.png';
                    } else {
                        var img = '../../../../../../assets/img/logo_itteh.png';
                    }

                    if (data.column.index == 9) {
                        var dim =
                            data.cell.height - data.cell.padding('vertical');
                        var textPos = data.cell;
                        var posX =
                            textPos.x + (data.cell.width / 2 - (dim - 1) / 2);
                        var posY =
                            textPos.y + (data.cell.height / 2 - (dim - 1) / 2);

                        doc.addImage(img, 'PNG', posX, posY, dim - 1, dim - 1);
                    } else {
                        var dim =
                            data.cell.height - data.cell.padding('vertical');
                        var textPos = data.cell;
                        var posX = textPos.x + 0.5;
                        var posY =
                            textPos.y + (data.cell.height / 2 - (dim - 1) / 2);

                        doc.addImage(
                            img,
                            'PNG',
                            posX,
                            posY,
                            data.cell.width - 1,
                            dim - 1
                        );
                    }
                }
            },
            body: [
                [
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                    {
                        content: '',
                        colSpan: 1,
                        styles: {
                            fillColor: 'white',
                            cellPadding: 4,
                        },
                    },
                ],
                [
                    {
                        content: `NO. DE SOLICITUD: ${solicitud}`,
                        colSpan: 20,
                        styles: {
                            halign: 'center',
                            fillColor: 'white',
                            fontStyle: 'bold',
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 20,
                        styles: {
                            cellPadding: 1,
                            fillColor: 'white',
                        },
                    },
                ],
                [
                    {
                        content: `DATOS ASPIRANTE:`,
                        colSpan: 20,
                        styles: {
                            halign: 'center',
                            fillColor: 'white',
                            textColor: [24, 61, 92],
                            fontStyle: 'bold',
                            fontSize: 9,
                        },
                    },
                ],
                [
                    {
                        content: `NOMBRE:`,
                        colSpan: 2,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${nombre.toUpperCase()}`,
                        colSpan: 12,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                    {
                        content: `CURP:`,
                        colSpan: 2,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${curp.toUpperCase()}`,
                        colSpan: 4,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                ],
                [
                    {
                        content: `DIRECCIÓN:`,
                        colSpan: 2,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${calle_no.toUpperCase()}`,
                        colSpan: 8,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                    {
                        content: `COLONIA/FRACC.:`,
                        colSpan: 3,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [250, 250, 250],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${colonia.toUpperCase()}`,
                        colSpan: 7,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                ],
                [
                    {
                        content: `CÓDIGO POSTAL:`,
                        colSpan: 3,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${codigo_postal.toUpperCase()}`,
                        colSpan: 2,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                    {
                        content: `ESTADO:`,
                        colSpan: 2,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${estado}`.toUpperCase(),
                        colSpan: 5,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                    {
                        content: `MUNICIPIO:`,
                        colSpan: 2,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${municipio.toUpperCase()}`,
                        colSpan: 7,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                ],
                [
                    {
                        content: `TELÉFONO:`,
                        colSpan: 2,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${telefono}`,
                        colSpan: 3,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                    {
                        content: ``,
                        colSpan: 15,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 20,
                        styles: {
                            cellPadding: 1,
                            fillColor: 'white',
                        },
                    },
                ],
                [
                    {
                        content: `DATOS ESCOLARES:`,
                        colSpan: 20,
                        styles: {
                            halign: 'center',
                            fillColor: 'white',
                            textColor: [24, 61, 92],
                            fontStyle: 'bold',
                            fontSize: 9,
                        },
                    },
                ],
                [
                    {
                        content: `CLAVE PREPA:`,
                        colSpan: 3,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${clave_prepa}`,
                        colSpan: 3,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                    {
                        content: `NOMBRE PREPA:`,
                        colSpan: 3,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [245, 245, 245],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: `${prepa}`,
                        colSpan: 11,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            lineWidth: 0.1,
                            lineColor: [245, 245, 245],
                            fillColor: [249, 249, 249],
                            fontSize: 8,
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 16,
                        styles: {
                            halign: 'left',
                            valign: 'middle',
                            fillColor: 'white',
                        },
                    },
                    {
                        content: `DOCUMENTO: CERTIFICADO`,
                        colSpan: 4,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            fillColor: 'white',
                            fontStyle: 'bold',
                            fontSize: 6,
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 20,
                        styles: {
                            cellPadding: 1,
                            fillColor: 'white',
                        },
                    },
                ],
                [
                    {
                        content: 'PRIMERA OPCIÓN',
                        colSpan: 10,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            fillColor: [24, 61, 92],
                            textColor: [255, 255, 255],
                            lineColor: [24, 61, 92],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                    {
                        content: 'SEGUNDA OPCIÓN',
                        colSpan: 10,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            fillColor: [24, 61, 92],
                            textColor: [255, 255, 255],
                            lineColor: [24, 61, 92],
                            fontStyle: 'bold',
                            fontSize: 7,
                        },
                    },
                ],
                [
                    {
                        content: `${op1}`,
                        colSpan: 10,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            fontSize: 7,
                            fontStyle: 'bold',
                            lineColor: [250, 250, 250],
                            fillColor: [245, 245, 245],
                        },
                    },
                    {
                        content: `${op2}`,
                        colSpan: 10,
                        styles: {
                            halign: 'center',
                            valign: 'middle',
                            fontSize: 7,
                            fontStyle: 'bold',
                            lineColor: [250, 250, 250],
                            fillColor: [245, 245, 245],
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 20,
                        styles: {
                            cellPadding: 1,
                            fillColor: 'white',
                        },
                    },
                ],
                [
                    {
                        content: 'CONTINUA CON LOS SIGUIENTES PUNTOS',
                        colSpan: 20,
                        styles: {
                            halign: 'center',
                            fillColor: 'white',
                            fontStyle: 'bold',
                            fontSize: 9,
                        },
                    },
                ],
                [
                    {
                        content: '1.-',
                        colSpan: 1,
                        styles: {
                            halign: 'center',
                            fillColor: 'white',
                            fontStyle: 'bold',
                            fontSize: 8,
                        },
                    },
                    {
                        //content: `EVUALUATEC ENVIARÁ LAS INDICACIONES AL CORREO QUE REGISTRASTE 3 DÍAS ANTES DEL EXÁMEN.`,
                        content: `ENTRASTE EN SEGUNDA ETAPA, TU FECHA DE EVALUACIÓN SERA EL 5 DE AGOSTO DEL PRESENTE AÑO. ESPERA INDICACIONES`,
                        colSpan: 19,
                        styles: {
                            halign: 'justify',
                            fillColor: 'white',
                            fontSize: 8,
                        },
                    },
                ],
            ],
        };

        autoTable(doc, table);
        doc.setTextColor('black');
        doc.setDocumentProperties({
            title: `- Solicitud Ingreso`,
        });

        const file: Blob = doc.output('blob');
        const filename: string = `${solicitud}-Solicitud.pdf`;

        return { file, filename };
    }

    generarPDFPosgrado(datos: Datos): PDF {
        const doc = new jsPDF({ orientation: 'p', format: 'letter' });
        moment.locale('es-mx');

        const fechaRegistro: string = moment(datos.asp.fecha_registro).format(
            'dddd LL[, a las ]LT'
        );

        doc.setFontSize(7);

        var fondo = '../../../../../../assets/img/fondo_posgrado.png';

        const solicitud: string = this.acompletarCeros(datos.asp.no_solicitud);

        const estado_nacimiento: string =
            datos.entidadesFederativas.find(
                (ef) => ef.clave == datos.gen.entidad_federativa_nacimiento
            )?.entidad_federativa ?? '';
        const estado: string =
            datos.entidadesFederativas.find(
                (ef) => ef.clave == datos.gen.entidad_federativa
            )?.entidad_federativa ?? '';
        const estado_emergencia: string =
            datos.entidadesFederativas.find(
                (ef) => ef.clave == datos.socieco.entidad_federativa
            )?.entidad_federativa ?? '';

        const op1: string =
            datos.carreras.find(
                (c) =>
                    c.carrera == datos.gen.carrera_opcion_1 &&
                    c.reticula == datos.gen.reticula_opcion_1
            )?.nombre_carrera ?? '';
        const op2: string =
            datos.carreras.find(
                (c) =>
                    c.carrera == datos.gen.carrera_opcion_2 &&
                    c.reticula == datos.gen.reticula_opcion_2
            )?.nombre_carrera ?? '';
        const año: number = parseInt(datos.asp.periodo.slice(0, 4));

        let table: UserOptions = {
            margin: { top: 2.5, left: 2, right: 2 },
            theme: 'plain',
            headStyles: {
                halign: 'center',
                valign: 'middle',
                minCellHeight: 5,
                cellPadding: 0.1,
                lineWidth: 0,
                // lineColor: 'black',
                // fillColor: 'white',
                textColor: 'black',
                fontSize: 8,
            },
            bodyStyles: {
                textColor: 'black',
                fontSize: 10,
                // fillColor: 'white',
                lineWidth: 0,
                // lineColor: 'black',
            },
            head: [
                [
                    {
                        content: '',
                        colSpan: 10,
                        styles: {
                            minCellHeight: 25,
                            fillColor: '#183d5c',
                            lineColor: '#183d5c',
                            lineWidth: 0.1,
                        },
                    },

                    {
                        content: '',
                        colSpan: 7,
                        styles: {
                            minCellHeight: 25,
                            fillColor: '#183d5c',
                            lineColor: '#183d5c',
                            lineWidth: 0.1,
                        },
                    },

                    {
                        content: '',
                        colSpan: 3,
                        styles: {
                            minCellHeight: 25,
                            fillColor: '#183d5c',
                            lineColor: '#183d5c',
                            lineWidth: 0.1,
                        },
                    },
                ],
                [
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                ],
            ],
            didDrawCell: (data) => {
                console.log(data);
                if (
                    (data.column.index == 0 ||
                        data.column.index == 10 ||
                        data.column.index == 17) &&
                    data.row.index == 0 &&
                    data.cell.section == 'head'
                ) {
                    if (data.column.index == 0) {
                        var img =
                            '../../../../../../assets/img/logo_educacion_sep_white.png';
                    } else if (data.column.index == 10) {
                        var img =
                            '../../../../../../assets/img/logo_tecnm_white.png';
                    } else {
                        var img =
                            '../../../../../../assets/img/logo_itteh_white.png';
                    }

                    var dim =
                        data.cell.height - data.cell.padding('vertical') - 1;
                    var textPos = data.cell;
                    if (data.column.index == 0) {
                        var imgDim = { width: dim, height: (dim * 158) / 600 };
                        var imgDim = { width: (dim * 600) / 158, height: dim };
                    } else if (data.column.index == 10) {
                        // var imgDim = { width: dim, height: (dim * 500) / 1180 };
                        var imgDim = { width: (dim * 1180) / 500, height: dim };
                    } else {
                        var imgDim = { width: dim, height: (dim * 500) / 500 };
                        // var imgDim = { width: (dim * 481) / 105, height: dim };
                    }

                    doc.addImage(
                        img,
                        'PNG',
                        textPos.x + (textPos.width - imgDim.width) / 2,
                        textPos.y + (textPos.height - imgDim.height) / 2,
                        imgDim.width,
                        imgDim.height
                    );
                }
            },
            body: [
                [
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                    {
                        content: '',
                    },
                ],
                [
                    {
                        content:
                            'DIVISIÓN DE ESTUDIOS DE POSGRADO E INVESTIGACIÓN',
                        colSpan: 20,
                        styles: {
                            fontSize: 12,
                            fontStyle: 'bold',
                            halign: 'center',
                        },
                    },
                ],
                [
                    {
                        content: 'SOLICITUD DE ADMISIÓN POSGRADO NUEVO INGRESO',
                        colSpan: 20,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            cellPadding: 0.5,
                        },
                    },
                ],
                [
                    {
                        content: op1,
                        colSpan: 20,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            cellPadding: 0.5,
                        },
                    },
                ],
                [
                    {
                        content: `CONVOCATORIA ${año}`,
                        colSpan: 20,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 20,
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 5,
                        rowSpan: 4,
                    },
                    {
                        content: `GENERACIÓN ${año}-${año + 2}`,
                        colSpan: 10,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#d9d9d9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: '',
                        colSpan: 5,
                        rowSpan: 4,
                    },
                ],
                [
                    {
                        content: '(Para ser llenado por la DEPI)',
                        colSpan: 10,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#d9d9d9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: 'Número de Control:',
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'left',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },

                    {
                        content: '',
                        colSpan: 6,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: 'Número de Expediente:',
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'left',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },

                    {
                        content: '',
                        colSpan: 6,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 20,
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 29,
                    },
                    {
                        content: `DATOS PERSONALES`,
                        colSpan: 16,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#d9d9d9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 29,
                    },
                ],
                [
                    {
                        content: `Nombre Completo`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Apellido Paterno`,
                        colSpan: 5,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Apellido Materno`,
                        colSpan: 5,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Nombres(s)`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.asp.apellido_paterno}`.toUpperCase(),
                        colSpan: 5,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.asp.apellido_materno}`.toUpperCase(),
                        colSpan: 5,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.asp.nombre_aspirante}`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Fecha de nacimiento`,
                        colSpan: 4,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `RFC`,
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `CURP`,
                        colSpan: 4,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Nacionalidad`,
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Sexo`,
                        colSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${moment(datos.gen.fecha_nacimiento).format(
                            'll'
                        )}`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.rfc}`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.asp.curp}`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.gen.entidad_federativa_nacimiento > 34
                                ? 'EXTRANJERA'
                                : 'MEXICANA'
                        }`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.sexo}`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Lugar de nacimiento`,
                        colSpan: 12,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Estado Civil`,
                        colSpan: 4,
                        rowSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            valign: 'middle',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Municipio`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Estado`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content:
                            `${datos.gen.municipio_nacimiento}`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${estado_nacimiento}`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${this.getEstadoCivil(
                            datos.gen.estado_civil
                        )}`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre del (la) cónyuge`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],

                [
                    {
                        content: `${datos.gen.nombre_conyuge ?? ''} ${
                            datos.gen.apellido_paterno_conyuge ?? ''
                        } ${
                            datos.gen.apellido_materno_conyuge ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Tiene hijos`,
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `No. hijos`,
                        colSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Edades`,
                        colSpan: 11,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.gen.hijos > 0 ? 'Si' : 'No'
                        }`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.gen.hijos > 0 ? datos.gen.hijos : ''
                        }`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.gen.hijos > 0
                                ? datos.gen.edades_hijos ?? ''
                                : ''
                        }`.toUpperCase(),
                        colSpan: 11,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Domicilio Actual`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Calle y Número`,
                        colSpan: 10,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Colonia`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.gen.calle_no}`.toUpperCase(),
                        colSpan: 10,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            valign: 'middle',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.colonia}`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            valign: 'middle',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Municipio`,
                        colSpan: 7,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `C.P.`,
                        colSpan: 2,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Estado`,
                        colSpan: 7,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.gen.municipio}`.toUpperCase(),
                        colSpan: 7,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.codigo_postal}`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${estado}`.toUpperCase(),
                        colSpan: 7,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Telefono Fijo`,
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Telefono Movil`,
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Correo Electronico`,
                        colSpan: 10,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.asp.telefono}`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.socieco.telefono}`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content:
                            `${datos.asp.correo_electronico}`.toUpperCase(),
                        colSpan: 10,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Contacto de Emergencia`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre`,
                        colSpan: 10,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Parentesco`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.socieco.comunicar_con}`.toUpperCase(),
                        colSpan: 10,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            this.parentesco[datos.socieco.parentesco - 1]
                        }`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Calle y Número`,
                        colSpan: 10,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Colonia`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.socieco.calle_no}`.toUpperCase(),
                        colSpan: 10,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            valign: 'middle',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.socieco.colonia}`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            valign: 'middle',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Municipio`,
                        colSpan: 7,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `C.P.`,
                        colSpan: 2,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Estado`,
                        colSpan: 7,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.socieco.municipio}`.toUpperCase(),
                        colSpan: 7,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.socieco.codigo_postal}`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${estado_emergencia}`.toUpperCase(),
                        colSpan: 7,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Telefono Fijo`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Telefono Movil`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Correo Electronico`,
                        colSpan: 10,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.socieco.telefono}`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content:
                            `${datos.socieco.telefono_trabajo}`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: ``.toUpperCase(),
                        colSpan: 10,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: ``.toUpperCase(),
                        colSpan: 20,
                        styles: {
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 15,
                    },
                    {
                        content: `DATOS ESCOLARES`,
                        colSpan: 16,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#d9d9d9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 15,
                    },
                ],
                [
                    {
                        content: `ESTUDIOS DE LICENCIATURA`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre de la Institución de Educación Superior donde realizó los estudios de licenciatura`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.gen.universidad}`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Licenciatura`,
                        colSpan: 8,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Especialidad`,
                        colSpan: 8,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.gen.licenciatura}`.toUpperCase(),
                        colSpan: 8,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.especialidad}`.toUpperCase(),
                        colSpan: 8,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Año Inicio`,
                        colSpan: 2,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Año Término`,
                        colSpan: 2,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Fecha Titulación`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Promedio Final`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Cedula Profesional`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${datos.gen.ano_ingreso}`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.ano_egreso}`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${moment(datos.gen.fecha_titulacion).format(
                            'll'
                        )}`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.promedio_general}`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.cedula}`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `ESTUDIOS DE LICENCIATURA`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre de la Institución de Educación Superior donde realizó los estudios de licenciatura`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.gen.universidad2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Licenciatura`,
                        colSpan: 8,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Especialidad`,
                        colSpan: 8,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.gen.licenciatura2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 8,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.gen.especialidad2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 8,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Año Inicio`,
                        colSpan: 2,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Año Término`,
                        colSpan: 2,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Fecha Titulación`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Promedio Final`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Cedula Profesional`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.gen.ano_ingreso2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.ano_egreso2 ?? ''}`.toUpperCase(),
                        colSpan: 2,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.gen.fecha_titulacion2
                                ? moment(datos.gen.fecha_titulacion2).format(
                                      'll'
                                  )
                                : ''
                        }`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.gen.promedio_general2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.gen.cedula2 ?? ''}`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: ``.toUpperCase(),
                        colSpan: 20,
                        styles: {
                            lineWidth: 0,
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 31,
                    },
                    {
                        content: `DATOS LABORALES`,
                        colSpan: 16,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#d9d9d9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 31,
                    },
                ],
                [
                    {
                        content: `Experiencia laboral`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${datos.socieco.experiencia_laboral ?? ''}`,
                        colSpan: 12,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `EMPLEO ACTUAL`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre de la Empresa`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.nombre_empresa ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Ubicación de la Empresa`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.ubicacion_empresa ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Puesto Desempeñado`,
                        colSpan: 12,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Fecha Inicio`,
                        colSpan: 4,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.puesto_empresa ?? ''
                        }`.toUpperCase(),
                        colSpan: 12,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.socieco.fecha_inicio_empresa
                                ? moment(
                                      datos.socieco.fecha_inicio_empresa
                                  ).format('ll')
                                : ''
                        }`.toUpperCase(),
                        colSpan: 4,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Horario`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.horario_laboral ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Actividades`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.actividades ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `EMPLEO ANTERIOR`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre de la Empresa`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.nombre_empresa_ant1 ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Ubicación de la Empresa`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.ubicacion_empresa_ant1 ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Puesto Desempeñado`,
                        colSpan: 10,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Fecha Inicio`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.puesto_empresa_ant1 ?? ''
                        }`.toUpperCase(),
                        colSpan: 10,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.socieco.periodo_empresa_ant1 ?? ''
                        }`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Actividades`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.actividades_ant1 ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `EMPLEO ANTERIOR`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre de la Empresa`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.nombre_empresa_ant2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Ubicación de la Empresa`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.ubicacion_empresa_ant2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Puesto Desempeñado`,
                        colSpan: 10,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Fecha Inicio`,
                        colSpan: 6,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.puesto_empresa_ant2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 10,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${
                            datos.socieco.periodo_empresa_ant2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 6,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Actividades`,
                        colSpan: 16,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#e9e9e9',
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${
                            datos.socieco.actividades_ant2 ?? ''
                        }`.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            fontSize: 10,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: ``.toUpperCase(),
                        colSpan: 20,
                        styles: {
                            lineWidth: 0,
                        },
                    },
                ],
                [
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 30,
                    },
                    {
                        content: `DOCUMENTACIÓN REQUERIDA PARA INSCRIPCIÓN`,
                        colSpan: 16,
                        styles: {
                            fontSize: 11,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            fillColor: '#d9d9d9',
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: '',
                        colSpan: 2,
                        rowSpan: 30,
                    },
                ],
                [
                    {
                        content: ``.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            lineWidth: 0,
                        },
                    },
                ],
                [
                    {
                        content: ``,
                        colSpan: 1,
                        rowSpan: 12,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `DOCUMENTO`,
                        colSpan: 11,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `TIPO`,
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: ``,
                        colSpan: 1,
                        rowSpan: 12,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0,
                        },
                    },
                ],
                [
                    {
                        content: `Acta de nacimiento`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original/Copia`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Certificado de licenciatura`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original/Copia`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Título y cédula profesional`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original/Copia`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `CURP`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original/Copia`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Identificación oficial (IFE/INE, licencia o pasaporte)`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original/Copia`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Comprobante de domicilio`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original/Copia`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `4 fotografías recientes (infantil o credencial, B/N o color)`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Curriculum vitae`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Carta de motivos para la obtención del grado`,
                        colSpan: 11,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Original`,
                        colSpan: 3,
                        styles: {
                            fontSize: 8,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.1,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `NOTAS:`,
                        colSpan: 14,
                        styles: {
                            fontSize: 8,
                            halign: 'left',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Los originales se usarán solo para cotejo y serán devueltos. Entregar una copia en Control Escolar y otra en la DEPI.`,
                        colSpan: 14,
                        styles: {
                            fontSize: 8,
                            halign: 'justify',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: ``.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            lineWidth: 0,
                        },
                    },
                ],
                [
                    {
                        content: `Por este medio, solicito la admisión al programa de ${op1}, y a la vez acepto la decisión que considere pertinente el Consejo de Posgrado para mi posible ingreso, de acuerdo con los resultados del examen de habilidades y la entrevista.`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'justify',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: ``.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            lineWidth: 0,
                        },
                    },
                ],
                [
                    {
                        content: `Declaro que la información proporcionada en esta solicitud es verídica y autorizo a la institución para su validación y verificación de la misma.`,
                        colSpan: 16,
                        styles: {
                            fontSize: 9,
                            halign: 'justify',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: ``.toUpperCase(),
                        colSpan: 16,
                        styles: {
                            lineWidth: 0,
                        },
                    },
                ],
                [
                    {
                        content: ``,
                        colSpan: 10,
                        rowSpan: 4,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.5,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Fecha`,
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Día`,
                        colSpan: 1,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.5,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Mes`,
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.5,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `Año`,
                        colSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0.5,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `${new Date().getDate()}`,
                        colSpan: 1,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.5,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${moment(new Date()).format(
                            'MMMM'
                        )}`.toUpperCase(),
                        colSpan: 3,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.5,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: `${new Date().getFullYear()}`,
                        colSpan: 2,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            cellPadding: 0.5,
                            lineWidth: 0.5,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: ``,
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
                [
                    {
                        content: `Nombre completo y firma`,
                        colSpan: 10,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                    {
                        content: ``,
                        colSpan: 6,
                        styles: {
                            fontSize: 9,
                            halign: 'center',
                            fontStyle: 'bold',
                            cellPadding: 0.5,
                            lineWidth: 0,
                            lineColor: '#d9d9d9',
                        },
                    },
                ],
            ],
        };

        autoTable(doc, table);

        const pages = doc.getNumberOfPages();

        for (let i = 1; i <= pages; i++) {
            doc.setPage(i);
            doc.addImage(fondo, 'PNG', 0, -25, 220, 352, undefined, 'FAST');
            doc.text(`REGISTRADO EL ${fechaRegistro.toUpperCase()}`, 10, 275);
            doc.text(`No. SOLICITUD ${solicitud.toUpperCase()}`, 175, 275);
        }

        doc.setPage(1);
        autoTable(doc, table);

        doc.setTextColor('black');

        doc.setDocumentProperties({
            title: `${solicitud}-Solicitud.pdf`,
        });

        const file: Blob = doc.output('blob');
        const filename: string = `${solicitud}-Solicitud.pdf`;

        return { file, filename };
    }

    acompletarCeros(numero: number): string {
        const numeroS = numero.toString();
        let nuevoNumero = '';

        switch (numeroS.length) {
            case 0:
                nuevoNumero = '00000000';
                break;
            case 1:
                nuevoNumero = '0000000' + numeroS;
                break;
            case 2:
                nuevoNumero = '000000' + numeroS;
                break;
            case 3:
                nuevoNumero = '00000' + numeroS;
                break;
            case 4:
                nuevoNumero = '0000' + numeroS;
                break;
            case 5:
                nuevoNumero = '000' + numeroS;
                break;
            case 6:
                nuevoNumero = '00' + numeroS;
                break;
            case 7:
                nuevoNumero = '0' + numeroS;
                break;
            default:
                nuevoNumero = numeroS;
        }

        return nuevoNumero;
    }

    getEstadoCivil(estado_civil: string): string {
        switch (estado_civil) {
            case 'S':
                return 'Soltero(a)';
            case 'C':
                return 'Casado(a)';
            case 'D':
                return 'Divorciado(a)';
            case 'V':
                return 'Viudo(a)';
            case 'U':
                return 'Unión libre';
            default:
                return 'Otro';
        }
    }
}
